/**
 * CampaignStepEditor - Inline content editor for campaign steps
 *
 * Email step: subject line + HTML body textarea + merge tag palette
 * SMS step: content textarea with character count + merge tags
 * Wait step: day picker
 */
import { useState } from 'react';
import { GripVertical, Trash2, Mail, MessageSquare, Clock, ChevronDown, Tag } from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@respectly/ui';
import type { CampaignStep, CampaignStepType } from '@respectly/shared-types';

// ============================================
// Merge Tags
// ============================================

const MERGE_TAGS = [
  { tag: '{{account.name}}', label: 'Account Name' },
  { tag: '{{account.ownerFirstName}}', label: 'Owner First Name' },
  { tag: '{{account.ownerLastName}}', label: 'Owner Last Name' },
  { tag: '{{account.ownerEmail}}', label: 'Owner Email' },
  { tag: '{{account.plan}}', label: 'Plan Name' },
  { tag: '{{account.slug}}', label: 'Account Slug' },
  { tag: '{{account.trialEndsAt}}', label: 'Trial End Date' },
  { tag: '{{platform.name}}', label: 'Platform Name' },
  { tag: '{{platform.supportEmail}}', label: 'Support Email' },
];

// ============================================
// Types
// ============================================

interface CampaignStepEditorProps {
  step: Partial<CampaignStep>;
  index: number;
  onChange: (step: Partial<CampaignStep>) => void;
  onDelete: () => void;
  isDragging?: boolean;
  className?: string;
}

// ============================================
// Configuration
// ============================================

const stepTypeConfig: Record<CampaignStepType, { label: string; icon: typeof Mail; borderColor: string; iconBg: string; iconColor: string }> = {
  email: {
    label: 'Send Email',
    icon: Mail,
    borderColor: 'border-indigo-200 dark:border-indigo-800',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/40',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
  },
  sms: {
    label: 'Send SMS',
    icon: MessageSquare,
    borderColor: 'border-purple-200 dark:border-purple-800',
    iconBg: 'bg-purple-100 dark:bg-purple-900/40',
    iconColor: 'text-purple-600 dark:text-purple-400',
  },
  wait: {
    label: 'Wait',
    icon: Clock,
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
};

// ============================================
// Merge Tag Palette
// ============================================

function MergeTagPalette({
  onInsert,
}: {
  onInsert: (tag: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
      >
        <Tag className="h-3 w-3" />
        Insert merge tag
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute top-6 left-0 z-10 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
          <div className="p-1.5">
            {MERGE_TAGS.map(({ tag, label }) => (
              <button
                key={tag}
                type="button"
                onClick={() => {
                  onInsert(tag);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 text-left group"
              >
                <span className="text-xs text-slate-600 dark:text-slate-300">{label}</span>
                <code className="text-[10px] font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-1 py-0.5 rounded opacity-75 group-hover:opacity-100">
                  {tag.replace('{{', '').replace('}}', '')}
                </code>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Component
// ============================================

export function CampaignStepEditor({
  step,
  index,
  onChange,
  onDelete,
  isDragging,
  className,
}: CampaignStepEditorProps) {
  const stepType = step.type || 'email';
  const config = stepTypeConfig[stepType];
  const Icon = config.icon;

  const smsLength = step.smsContent?.length ?? 0;
  const smsSegments = Math.ceil(smsLength / 160) || 1;

  const insertTag = (field: 'emailSubject' | 'emailContent' | 'smsContent', tag: string) => {
    const current = (step[field] as string | null | undefined) ?? '';
    onChange({ ...step, [field]: current + tag });
  };

  return (
    <Card
      className={cn(
        'relative border-l-4 transition-all',
        config.borderColor,
        isDragging && 'shadow-lg opacity-90',
        className
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Drag handle + step number */}
          <div className="flex flex-col items-center gap-1.5 pt-0.5 flex-shrink-0">
            <button
              type="button"
              className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 transition-colors"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 tabular-nums">
              {String(index + 1).padStart(2, '0')}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* Step type row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn('p-1.5 rounded-md', config.iconBg)}>
                  <Icon className={cn('h-4 w-4', config.iconColor)} />
                </div>
                <Select
                  value={stepType}
                  onValueChange={(val) => onChange({ ...step, type: val as CampaignStepType })}
                >
                  <SelectTrigger className="w-[140px] h-8 border-0 bg-transparent font-semibold text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(stepTypeConfig).map(([value, cfg]) => (
                      <SelectItem key={value} value={value}>
                        <div className="flex items-center gap-2">
                          <cfg.icon className="h-4 w-4" />
                          {cfg.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="h-8 w-8 p-0 text-slate-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Email fields */}
            {stepType === 'email' && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">Subject Line</Label>
                    <MergeTagPalette onInsert={(tag) => insertTag('emailSubject', tag)} />
                  </div>
                  <Input
                    placeholder="e.g., Welcome to {{platform.name}}, {{account.ownerFirstName}}!"
                    value={step.emailSubject ?? ''}
                    onChange={(e) => onChange({ ...step, emailSubject: e.target.value || null })}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">Email Body (HTML)</Label>
                    <MergeTagPalette onInsert={(tag) => insertTag('emailContent', tag)} />
                  </div>
                  <Textarea
                    placeholder={`<p>Hi {{account.ownerFirstName}},</p>\n<p>Welcome to {{platform.name}}! Here's how to get started...</p>`}
                    value={step.emailContent ?? ''}
                    onChange={(e) => onChange({ ...step, emailContent: e.target.value || null })}
                    rows={6}
                    className="text-sm font-mono resize-y min-h-[120px]"
                  />
                  <p className="text-xs text-slate-400">
                    Supports HTML. Use merge tags like{' '}
                    <code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded text-purple-600 dark:text-purple-400">
                      {'{{account.name}}'}
                    </code>{' '}
                    for personalization.
                  </p>
                </div>
              </div>
            )}

            {/* SMS fields */}
            {stepType === 'sms' && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">SMS Content</Label>
                  <MergeTagPalette onInsert={(tag) => insertTag('smsContent', tag)} />
                </div>
                <Textarea
                  placeholder={`Hi {{account.ownerFirstName}}, your {{platform.name}} trial ends on {{account.trialEndsAt}}. Upgrade now to keep access.`}
                  value={step.smsContent ?? ''}
                  onChange={(e) => onChange({ ...step, smsContent: e.target.value || null })}
                  rows={4}
                  className="text-sm resize-none"
                  maxLength={1600}
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400">Plain text. Merge tags supported.</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={cn(
                        'tabular-nums',
                        smsLength > 160 ? 'text-amber-500' : 'text-slate-400'
                      )}
                    >
                      {smsLength} chars
                    </span>
                    {smsLength > 0 && (
                      <span className="text-slate-400">
                        ·{' '}
                        <span className={smsSegments > 1 ? 'text-amber-500' : 'text-slate-400'}>
                          {smsSegments} segment{smsSegments !== 1 ? 's' : ''}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Wait fields */}
            {stepType === 'wait' && (
              <div className="flex items-end gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    Wait Duration
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={step.delay ?? 1}
                      onChange={(e) => onChange({ ...step, delay: parseInt(e.target.value) || 1 })}
                      className="w-20 text-center tabular-nums"
                    />
                    <span className="text-sm text-slate-500">
                      {(step.delay ?? 1) === 1 ? 'day' : 'days'} before next step
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
